import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { ArrowLeft, Clock, Send, Mail, Eye, RefreshCw, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MessagingPanel({ eventId, event, messages, onRefresh }) {
  const [showComposer, setShowComposer] = useState(false);
  const [composerForm, setComposerForm] = useState({ subject: '', body: '', audience: 'access_granted', scheduled_at: '' });
  const [composerSending, setComposerSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [recipientCount, setRecipientCount] = useState(null);
  const [sendingMsg, setSendingMsg] = useState(null);
  const [resendingMsg, setResendingMsg] = useState(null);

  const MESSAGE_TEMPLATES = [
    { label: 'Event Reminder', subject: 'Event Reminder', body: `Hi there!\n\nJust a friendly reminder that ${event?.title || 'our event'} is coming up soon. Make sure you have your digital pass ready for quick entry.\n\nSee you there!` },
    { label: 'Dress Code', subject: 'Dress Code Info', body: `Hey!\n\nHere's the dress code for ${event?.title || 'the event'}:\n\n[Enter dress code details here]\n\nLooking forward to seeing you!` },
    { label: 'Schedule Change', subject: 'Schedule Update', body: `Important update for ${event?.title || 'the event'}:\n\n[Enter the schedule change details here]\n\nPlease take note of this change. Thank you!` },
    { label: 'Important Update', subject: 'Important Event Update', body: `Hi!\n\nWe have an important update regarding ${event?.title || 'the event'}:\n\n[Enter update details here]\n\nThank you for your understanding.` },
    { label: 'Location Reminder', subject: 'Location Details', body: `Here are the location details for ${event?.title || 'the event'}:\n\n${event?.location || '[Location]'}\n\nSee you soon!` },
  ];

  const getAudienceLabel = (val) => {
    if (val === 'access_granted') return 'Approved Guests';
    if (val === 'pending') return 'Pending Guests';
    if (val === 'all') return 'All Ticket Holders';
    if (val?.startsWith('tier:')) {
      const tid = val.split(':')[1];
      const tier = (event?.pass_tiers || []).find(t => t.id === tid);
      return tier ? `${tier.tier_name} holders` : 'Tier';
    }
    return val;
  };

  const handleOpenPreview = async () => {
    if (!composerForm.subject.trim() || !composerForm.body.trim()) {
      toast.error('Subject and message body are required to preview');
      return;
    }
    setRecipientCount(null);
    setShowPreview(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/events/${eventId}/message-recipient-count`, {
        params: { audience: composerForm.audience },
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipientCount(res.data.count);
    } catch {
      setRecipientCount(-1);
    }
  };

  const handleComposerSend = async (sendNow) => {
    if (!composerForm.subject.trim() || !composerForm.body.trim()) {
      toast.error('Subject and message body are required');
      return;
    }
    if (!sendNow && !composerForm.scheduled_at) {
      toast.error('Select a scheduled time');
      return;
    }
    setComposerSending(true);
    try {
      const token = localStorage.getItem('session_token');
      const payload = {
        message_type: 'custom',
        subject: composerForm.subject,
        body: composerForm.body,
        audience: composerForm.audience,
        status: sendNow ? 'draft' : 'scheduled',
        scheduled_at: sendNow ? null : new Date(composerForm.scheduled_at).toISOString(),
      };
      const res = await axios.post(`${API}/events/${eventId}/messages`, payload, { headers: { Authorization: `Bearer ${token}` } });
      if (sendNow) {
        const sendRes = await axios.post(`${API}/events/${eventId}/messages/${res.data.message_id}/send`, {}, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(sendRes.data.message);
      } else {
        toast.success('Message scheduled');
      }
      setShowComposer(false);
      setComposerForm({ subject: '', body: '', audience: 'access_granted', scheduled_at: '' });
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
    finally { setComposerSending(false); }
  };

  const handleSendMessage = async (msgId) => {
    if (!confirm('Send this message now?')) return;
    setSendingMsg(msgId);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${API}/events/${eventId}/messages/${msgId}/send`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setSendingMsg(null); }
  };

  const handleCancelMessage = async (msgId) => {
    if (!confirm('Cancel this scheduled message?')) return;
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/events/${eventId}/messages/${msgId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Message cancelled');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleResendFailed = async (msgId) => {
    if (!confirm('Resend to failed recipients?')) return;
    setResendingMsg(msgId);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${API}/events/${eventId}/messages/${msgId}/resend-failed`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Resend failed'); }
    finally { setResendingMsg(null); }
  };

  return (
    <>
      {/* Message Guests */}
      <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm" data-testid="message-guests-section">
        <div className="p-4 border-b border-[#1A1A1A] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[#EAE7E1] flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#D4AF37]" /> Message Guests
          </h2>
          {!showComposer && (
            <Button onClick={() => setShowComposer(true)} className="bg-[#C6A66B] hover:bg-[#D4AF37] text-[#050505] rounded-sm font-bold" data-testid="open-composer-button">
              <Send className="w-4 h-4 mr-2" /> Compose
            </Button>
          )}
        </div>

        {showComposer && (
          <div className="p-4 border-b border-[#1A1A1A] bg-[#050505] space-y-4" data-testid="message-composer">
            <div>
              <Label className="text-[#EAE7E1] text-sm font-medium mb-2 block">Quick Templates</Label>
              <div className="flex flex-wrap gap-2" data-testid="template-buttons">
                {MESSAGE_TEMPLATES.map((t) => (
                  <button
                    key={t.subject}
                    type="button"
                    onClick={() => setComposerForm(prev => ({ ...prev, subject: t.subject, body: t.body }))}
                    className={`px-3 py-1.5 rounded-sm border text-xs font-medium transition-all ${composerForm.subject === t.subject ? 'border-[#C6A66B] bg-[#C6A66B]/10 text-[#C6A66B]' : 'border-[#1A1A1A] text-[#777] hover:border-[#333] hover:text-[#AFAFAF]'}`}
                    data-testid={`template-${t.subject.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[#EAE7E1] text-sm">Audience *</Label>
              <select
                value={composerForm.audience}
                onChange={e => setComposerForm(prev => ({ ...prev, audience: e.target.value }))}
                className="w-full mt-1 px-3 py-2 bg-[#050505] border border-[#1A1A1A] rounded-sm text-[#EAE7E1] focus:border-[#D4AF37]"
                data-testid="audience-select"
              >
                <option value="access_granted">Approved Guests</option>
                <option value="pending">Pending Guests</option>
                <option value="all">All Ticket Holders</option>
                {(event?.pass_tiers || []).filter(t => t.type === 'entry').map(t => (
                  <option key={t.id} value={`tier:${t.id}`}>{t.tier_name} holders</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[#EAE7E1] text-sm">Subject *</Label>
              <Input
                value={composerForm.subject}
                onChange={e => setComposerForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Message subject"
                className="mt-1 bg-[#050505] border-[#1A1A1A] text-[#EAE7E1] focus:border-[#D4AF37]"
                data-testid="composer-subject"
              />
            </div>
            <div>
              <Label className="text-[#EAE7E1] text-sm">Message *</Label>
              <Textarea
                value={composerForm.body}
                onChange={e => setComposerForm(prev => ({ ...prev, body: e.target.value }))}
                placeholder="Write your message to guests..."
                rows={6}
                className="mt-1 bg-[#050505] border-[#1A1A1A] text-[#EAE7E1] focus:border-[#D4AF37]"
                data-testid="composer-body"
              />
            </div>
            <div>
              <Label className="text-[#EAE7E1] text-sm">Schedule for later (optional)</Label>
              <Input
                type="datetime-local"
                value={composerForm.scheduled_at}
                onChange={e => setComposerForm(prev => ({ ...prev, scheduled_at: e.target.value }))}
                className="mt-1 bg-[#050505] border-[#1A1A1A] text-[#EAE7E1]"
                data-testid="composer-schedule"
              />
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={() => handleComposerSend(true)} disabled={composerSending} className="flex-1 bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm font-bold" data-testid="send-now-button">
                <Send className="w-4 h-4 mr-2" /> {composerSending ? 'Sending...' : 'Send Now'}
              </Button>
              {composerForm.scheduled_at && (
                <Button onClick={() => handleComposerSend(false)} disabled={composerSending} variant="outline" className="flex-1 border-[#C6A66B]/40 text-[#C6A66B] rounded-sm font-bold" data-testid="schedule-button">
                  <Clock className="w-4 h-4 mr-2" /> Schedule
                </Button>
              )}
              <Button onClick={handleOpenPreview} variant="outline" className="border-[#C6A66B]/30 text-[#C6A66B] hover:bg-[#C6A66B]/10 rounded-sm" data-testid="preview-button">
                <Eye className="w-4 h-4 mr-2" /> Preview
              </Button>
              <Button onClick={() => { setShowComposer(false); setComposerForm({ subject: '', body: '', audience: 'access_granted', scheduled_at: '' }); }} variant="outline" className="border-[#1A1A1A] text-[#777]" data-testid="cancel-composer-button">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="preview-modal">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowPreview(false)} />
            <div className="relative w-full max-w-lg max-h-[85vh] flex flex-col bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-[#1A1A1A] flex-shrink-0">
                <h3 className="text-sm font-semibold text-[#EAE7E1] flex items-center gap-2">
                  <Eye className="w-4 h-4 text-[#D4AF37]" /> Email Preview
                </h3>
                <button onClick={() => setShowPreview(false)} className="text-[#555] hover:text-[#EAE7E1] transition-colors" data-testid="close-preview">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="px-4 py-3 border-b border-[#1A1A1A] flex-shrink-0 space-y-1.5">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-[#777]">To:</span>
                  <span className="px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded text-[10px] font-semibold uppercase">{getAudienceLabel(composerForm.audience)}</span>
                  <span className="text-[#777]" data-testid="recipient-count">
                    {recipientCount === null ? (
                      <span className="inline-block w-3 h-3 border-2 border-[#555] border-t-[#D4AF37] rounded-full animate-spin" />
                    ) : recipientCount === -1 ? '' : (
                      `~${recipientCount} recipient${recipientCount !== 1 ? 's' : ''}`
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-[#777]">Mode:</span>
                  {composerForm.scheduled_at
                    ? <span className="text-[#AFAFAF]">Scheduled for {new Date(composerForm.scheduled_at).toLocaleString()}</span>
                    : <span className="text-[#AFAFAF]">Send immediately</span>
                  }
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4" style={{ background: '#050505' }}>
                <div style={{ maxWidth: 480, margin: '0 auto', background: '#0A0A0A', border: '1px solid #1A1A1A', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ padding: '32px 24px', textAlign: 'center', borderBottom: '1px solid rgba(198,166,107,0.15)' }}>
                    <div style={{ fontSize: 12, letterSpacing: 3, color: '#C6A66B', textTransform: 'uppercase', fontWeight: 600, marginBottom: 8 }}>{composerForm.subject}</div>
                    {event?.title && <div style={{ fontSize: 18, fontWeight: 700, color: '#EAE7E1' }}>{event.title}</div>}
                  </div>
                  <div style={{ padding: '28px 24px' }}>
                    <div style={{ fontSize: 14, color: '#B9B1A6', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{composerForm.body}</div>
                  </div>
                  <div style={{ padding: '16px 24px', background: '#050505', textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: '#333' }}>VelvetGate</div>
                  </div>
                </div>
              </div>
              <div className="p-4 border-t border-[#1A1A1A] flex-shrink-0 flex flex-wrap gap-2">
                <Button onClick={() => setShowPreview(false)} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] rounded-sm" data-testid="preview-back-button">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Edit
                </Button>
                <div className="flex-1" />
                {composerForm.scheduled_at && (
                  <Button onClick={() => { setShowPreview(false); handleComposerSend(false); }} disabled={composerSending} variant="outline" className="border-[#C6A66B]/40 text-[#C6A66B] rounded-sm font-bold" data-testid="preview-schedule-button">
                    <Clock className="w-4 h-4 mr-2" /> Schedule
                  </Button>
                )}
                <Button onClick={() => { setShowPreview(false); handleComposerSend(true); }} disabled={composerSending} className="bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm font-bold" data-testid="preview-send-button">
                  <Send className="w-4 h-4 mr-2" /> {composerSending ? 'Sending...' : 'Send Now'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {!showComposer && messages.length === 0 && (
          <div className="p-6 text-center text-[#555] text-sm">No messages sent yet. Compose a message to communicate with your guests.</div>
        )}
      </div>

      {/* Message History */}
      {messages.length > 0 && (
        <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm" data-testid="messages-section">
          <div className="p-4 border-b border-[#1A1A1A]">
            <h2 className="text-lg font-semibold text-[#EAE7E1] flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#D4AF37]" /> Message History
            </h2>
          </div>
          <div className="divide-y divide-[#1A1A1A]">
            {messages.map(msg => {
              const statusColors = {
                draft: 'bg-[#1A1A1A] text-[#777]',
                scheduled: 'bg-[#D4AF37]/15 text-[#D4AF37]',
                sending: 'bg-blue-900/30 text-blue-400',
                sent: 'bg-green-900/30 text-green-400',
                failed: 'bg-red-900/30 text-red-400',
                cancelled: 'bg-[#1A1A1A] text-[#555] line-through',
              };
              const audienceLabel = msg.audience === 'access_granted' ? 'Approved' : msg.audience === 'pending' ? 'Pending' : msg.audience === 'all' ? 'All' : msg.audience?.startsWith('tier:') ? 'Tier' : msg.audience;
              return (
                <div key={msg.message_id} className="p-4" data-testid={`message-${msg.message_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${statusColors[msg.status] || statusColors.draft}`}>{msg.status}</span>
                        {msg.message_type === 'location_reveal' && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#6D1ED4]/15 text-[#A78BFA]">Location Reveal</span>}
                        <span className="text-[9px] font-medium uppercase px-1.5 py-0.5 rounded bg-[#1A1A1A] text-[#777]">{audienceLabel}</span>
                      </div>
                      <div className="text-sm font-medium text-[#EAE7E1] mt-1">{msg.subject}</div>
                      {msg.scheduled_at && msg.status === 'scheduled' && (
                        <div className="text-xs text-[#777] mt-1">Scheduled: {new Date(msg.scheduled_at).toLocaleString()}</div>
                      )}
                      {msg.sent_at && <div className="text-xs text-[#6EE7B7] mt-1">Sent: {new Date(msg.sent_at).toLocaleString()} ({msg.recipients_count} recipient{msg.recipients_count !== 1 ? 's' : ''})</div>}
                      {msg.failure_reason && <div className="text-xs text-red-400 mt-1">Error: {msg.failure_reason}</div>}
                      {(msg.failed_recipients?.length > 0) && (
                        <div className="text-xs text-red-400 mt-1" data-testid={`failed-count-${msg.message_id}`}>
                          {msg.failed_recipients.length} failed deliver{msg.failed_recipients.length !== 1 ? 'ies' : 'y'}
                        </div>
                      )}
                      {msg.last_resend_at && (
                        <div className="text-xs text-[#777] mt-1" data-testid={`resent-info-${msg.message_id}`}>
                          Resent {new Date(msg.last_resend_at).toLocaleString()} (attempt {msg.resend_count})
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0 flex-wrap justify-end">
                      {(msg.failed_recipients?.length > 0) && msg.status === 'sent' && (
                        <Button size="sm" onClick={() => handleResendFailed(msg.message_id)} disabled={resendingMsg === msg.message_id} variant="outline" className="border-red-900/40 text-red-400 hover:bg-red-900/20 rounded-sm text-xs" data-testid={`resend-failed-${msg.message_id}`}>
                          <RefreshCw className={`w-3 h-3 mr-1 ${resendingMsg === msg.message_id ? 'animate-spin' : ''}`} />
                          {resendingMsg === msg.message_id ? 'Resending...' : 'Resend Failed'}
                        </Button>
                      )}
                      {['draft', 'scheduled', 'failed'].includes(msg.status) && (
                        <Button size="sm" onClick={() => handleSendMessage(msg.message_id)} disabled={sendingMsg === msg.message_id} className="bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm text-xs" data-testid={`send-msg-${msg.message_id}`}>
                          {sendingMsg === msg.message_id ? '...' : 'Send Now'}
                        </Button>
                      )}
                      {['draft', 'scheduled'].includes(msg.status) && (
                        <Button size="sm" variant="outline" onClick={() => handleCancelMessage(msg.message_id)} className="border-[#1A1A1A] text-[#777] hover:text-[#C44545] rounded-sm text-xs" data-testid={`cancel-msg-${msg.message_id}`}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
